import axios from 'axios';
import React, { useState, useEffect, useMemo } from 'react';
import Dashboard from './Dashboard'; // Importe o novo componente Dashboard
import ChatList from './Chats/ChatList';
import ChatWindow from './Chats/ChatWindow';
import ChangePassword from './Auth/ChangePassword';
import TicketManager from './Tickets/TicketManager';
import ReportManager from './Reports/ReportManager';
import SettingsPanel from './Settings/SettingsPanel';
import CadastroPanel from './CRM/CadastroPanel';
import KanbanBoard from './Kanban/KanbanBoard';
import NpsManager from './NPS/NpsManager';
import KnowledgeManager from './Knowledge/KnowledgeManager';
import useChats from '../hooks/useChats';
import useSocket from '../hooks/useSocket';
import { apiBaseUrl, authBaseUrl } from '../config/runtime';
import '../styles/App.css';

const FINAL_QUEUE_STATUSES = new Set(['finalizado', 'em_finalizacao', 'encerrado']);
const ASSIGNMENT_QUEUE_STATUSES = new Set(['com_robo', 'em_disparo']);
const MULTI_QUEUE_DEFINITIONS = [
  { id: 'meus_chats', label: 'Meus Chats' },
  { id: 'nao_atribuidos', label: 'Nao Atribuidos', statuses: ['com_robo', 'em_disparo'] },
  { id: 'em_aberto', label: 'Em Aberto', statuses: ['em_aberto', 'visualizado'] },
  { id: 'resolvido', label: 'Resolvido', statuses: ['resolvido'] },
  { id: 'ag_issue_correcao', label: 'Ag. Issue Correcao', statuses: ['ag_issue_correcao'] },
  { id: 'ag_envio', label: 'Ag. Envio', statuses: ['ag_envio'] },
  { id: 'enviado', label: 'Enviado', statuses: ['enviado'] },
  { id: 'pendente_retorno_cliente', label: 'Pend. Retorno Cliente', statuses: ['pendente_retorno_cliente'] },
  { id: 'suspenso', label: 'Suspenso', statuses: ['suspenso'], managerOnly: true },
  { id: 'em_finalizacao', label: 'Em Finalizacao', statuses: ['em_finalizacao'] },
  { id: 'encerrado', label: 'Encerrado', statuses: ['encerrado'], managerOnly: true },
  { id: 'finalizado', label: 'Finalizado', statuses: ['finalizado'] },
  { id: 'todos', label: 'Todos', showAll: true, managerOnly: true },
];

const TAB_DEFINITIONS = [
  { id: 'dashboard', label: 'Dashboard' },
  { id: 'projetos', label: 'Projetos' },
  { id: 'projetos_rma', label: 'Projetos RMA/HW' },
  { id: 'chamados', label: 'Multi atendimento' },
  { id: 'gestao', label: 'Gestão' },
  { id: 'cadastros', label: 'Cadastros' },
  { id: 'knowledge', label: 'Base Conhecimento' },
  { id: 'satisfacao', label: 'Satisfação' },
  { id: 'relatorios', label: 'Relatórios' },
  { id: 'comercial', label: 'Comercial' },
  { id: 'configuracoes', label: 'Configurações', config: true },
];

const FALLBACK_ACCESS_CONTROL = {
  area_flags: {
    operacional: true,
    tecnico: true,
    ti: true,
    gestao: true,
    comercial: false,
  },
  module_access: {
    operacional: ['dashboard', 'chamados', 'gestao', 'satisfacao'],
    tecnico: ['dashboard', 'projetos', 'projetos_rma', 'knowledge', 'relatorios'],
    ti: ['dashboard', 'projetos', 'projetos_rma', 'knowledge', 'relatorios'],
    gestao: ['dashboard', 'chamados', 'gestao', 'projetos', 'projetos_rma', 'cadastros', 'knowledge', 'satisfacao', 'relatorios'],
    comercial: ['dashboard', 'cadastros', 'relatorios', 'comercial'],
  },
};

const AREA_CARDS = {
  full: {
    title: 'Painel Completo',
    description: 'Acesso total aos modulos para administracao geral.',
  },
  operacional: {
    title: 'Operacional',
    description: 'Multi atendimento, gestao de chamados e satisfacao.',
  },
  gestao: {
    title: 'Gestao',
    description: 'Visao executiva de ITIL, SLA e governanca de chamados.',
  },
  tecnico: {
    title: 'Tecnico',
    description: 'Projetos SW, conhecimento e relatorios tecnicos.',
  },
  ti: {
    title: 'TI',
    description: 'Fluxos internos de infraestrutura e atendimento tecnico.',
  },
  comercial: {
    title: 'Comercial',
    description: 'CRM comercial (em construcao) e pipeline de vendas.',
  },
};

const VALID_AREAS = ['operacional', 'comercial', 'tecnico', 'ti', 'gestao'];

const CommercialPlaceholder = () => (
  <div className="content">
    <div className="ticket-manager">
      <div className="ticket-manager__header">
        <div className="ticket-manager__hero">
          <div className="ticket-manager__title">
            <h2>CRM Comercial</h2>
            <p>Modulo comercial em construcao. Em breve com funil, propostas e conversao de leads.</p>
          </div>
        </div>
      </div>
    </div>
  </div>
);

const MainContent = ({ socket, handleLogout, userId, role }) => {
  const [activeTab, setActiveTab] = useState('chamados');
  const [chatCategory, setChatCategory] = useState('em_aberto');
  const [selectedChat, setSelectedChat] = useState(null);
  const [selectedChatId, setSelectedChatId] = useState(null); // Estado para armazenar o chatId selecionado
  const [showChangePassword, setShowChangePassword] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [errorMessage, setErrorMessage] = useState(''); // Novo estado para armazenar mensagens de erro
  const [accessControl, setAccessControl] = useState(FALLBACK_ACCESS_CONTROL);
  const [accessLoaded, setAccessLoaded] = useState(false);
  const [showAreaSwitcher, setShowAreaSwitcher] = useState(false);
  const [activeArea, setActiveArea] = useState(() => String(localStorage.getItem('activeArea') || 'auto').toLowerCase());

  const { chats, updateChats, markAsRead } = useChats(socket, true, userId, chatCategory);
  const normalizedRole = String(role || localStorage.getItem('role') || '').toLowerCase();
  const normalizedDepartment = String(localStorage.getItem('department') || 'operacional').toLowerCase();
  const token = localStorage.getItem('token') || '';
  const companyCode = localStorage.getItem('companyCode') || '';
  const isAdminRole = normalizedRole === 'admin' || normalizedRole === 'master';
  const isSupervisorRole = normalizedRole === 'supervisor';
  const isManagementRole = normalizedRole === 'admin' || normalizedRole === 'supervisor' || normalizedRole === 'master';
  const availableChatCategories = useMemo(
    () => MULTI_QUEUE_DEFINITIONS.filter((queue) => !queue.managerOnly || isManagementRole),
    [isManagementRole]
  );

  useEffect(() => {
    if (!availableChatCategories.some((queue) => queue.id === chatCategory)) {
      setChatCategory(availableChatCategories[0]?.id || 'em_aberto');
    }
  }, [availableChatCategories, chatCategory]);

  useEffect(() => {
    let mounted = true;
    const loadAccessControl = async () => {
      if (!token) {
        if (mounted) {
          setAccessControl(FALLBACK_ACCESS_CONTROL);
          setAccessLoaded(true);
        }
        return;
      }
      try {
        const response = await axios.get(`${authBaseUrl}/access/control`, {
          headers: {
            Authorization: `Bearer ${token}`,
            ...(companyCode ? { 'X-Company-Code': companyCode } : {}),
          },
        });
        if (!mounted) return;
        setAccessControl({
          area_flags: { ...FALLBACK_ACCESS_CONTROL.area_flags, ...(response.data?.area_flags || {}) },
          module_access: { ...FALLBACK_ACCESS_CONTROL.module_access, ...(response.data?.module_access || {}) },
        });
      } catch (error) {
        if (!mounted) return;
        setAccessControl(FALLBACK_ACCESS_CONTROL);
      } finally {
        if (mounted) {
          setAccessLoaded(true);
        }
      }
    };
    loadAccessControl();
    return () => {
      mounted = false;
    };
  }, [token, companyCode]);

  useEffect(() => {
    const fallbackArea = VALID_AREAS.includes(normalizedDepartment) ? normalizedDepartment : 'operacional';
    const normalizedCurrent = String(activeArea || '').toLowerCase();
    if (isAdminRole) {
      if (normalizedCurrent === 'full' || VALID_AREAS.includes(normalizedCurrent)) {
        return;
      }
      setActiveArea('full');
      localStorage.setItem('activeArea', 'full');
      return;
    }
    const nextArea = isSupervisorRole && VALID_AREAS.includes(normalizedCurrent) ? normalizedCurrent : fallbackArea;
    if (normalizedCurrent !== nextArea) {
      setActiveArea(nextArea);
      localStorage.setItem('activeArea', nextArea);
    }
  }, [activeArea, isAdminRole, isSupervisorRole, normalizedDepartment]);

  const areaOptions = useMemo(() => {
    const areaFlags = accessControl?.area_flags || FALLBACK_ACCESS_CONTROL.area_flags;
    const options = VALID_AREAS.map((areaId) => {
      const enabledByTenant = Boolean(areaFlags[areaId]);
      const enabledByRole = isAdminRole || isSupervisorRole || areaId === normalizedDepartment;
      const enabled = enabledByTenant && enabledByRole;
      return {
        id: areaId,
        title: AREA_CARDS[areaId]?.title || areaId,
        description: AREA_CARDS[areaId]?.description || '',
        enabled,
        reason: enabledByTenant ? (enabledByRole ? '' : 'Nao liberado para seu perfil') : 'Area indisponivel para sua empresa',
      };
    });

    if (isAdminRole) {
      options.unshift({
        id: 'full',
        title: AREA_CARDS.full.title,
        description: AREA_CARDS.full.description,
        enabled: true,
        reason: '',
      });
    }
    return options;
  }, [accessControl, isAdminRole, isSupervisorRole, normalizedDepartment]);

  useEffect(() => {
    if (!accessLoaded) return;
    const selected = areaOptions.find((option) => option.id === activeArea && option.enabled);
    if (selected) return;
    const fallback = areaOptions.find((option) => option.enabled)?.id;
    if (!fallback) return;
    setActiveArea(fallback);
    localStorage.setItem('activeArea', fallback);
  }, [areaOptions, activeArea, accessLoaded]);

  const allowedTabs = useMemo(() => {
    const allTabs = TAB_DEFINITIONS.map((tab) => tab.id);
    const areaFlags = accessControl?.area_flags || FALLBACK_ACCESS_CONTROL.area_flags;
    const moduleAccess = accessControl?.module_access || FALLBACK_ACCESS_CONTROL.module_access;
    const fallbackArea = VALID_AREAS.includes(normalizedDepartment) ? normalizedDepartment : 'operacional';
    const selectedArea = VALID_AREAS.includes(activeArea)
      ? activeArea
      : fallbackArea;

    if (isAdminRole && activeArea === 'full') {
      return allTabs;
    }

    const isAreaEnabled = Boolean(areaFlags[selectedArea]);
    if (!isAreaEnabled) {
      return isAdminRole ? ['dashboard', 'configuracoes'] : ['dashboard'];
    }

    const configuredTabs = moduleAccess[selectedArea] || FALLBACK_ACCESS_CONTROL.module_access[selectedArea] || ['dashboard'];
    const baseTabs = isAdminRole
      ? ['dashboard', 'configuracoes', ...configuredTabs]
      : ['dashboard', ...configuredTabs];
    const deduped = Array.from(new Set(baseTabs));
    return deduped.filter((tabId) => TAB_DEFINITIONS.some((tab) => tab.id === tabId));
  }, [isAdminRole, accessControl, normalizedDepartment, activeArea]);

  useEffect(() => {
    if (!allowedTabs.includes(activeTab)) {
      setActiveTab(allowedTabs[0] || 'dashboard');
    }
  }, [activeTab, allowedTabs]);

  const visibleTabs = useMemo(
    () => TAB_DEFINITIONS.filter((tab) => allowedTabs.includes(tab.id)),
    [allowedTabs]
  );

  // Lida com a exibição da mensagem de erro
  const handleError = (errorData) => {
    setErrorMessage(`Erro ao finalizar o chat: ${errorData.message}`);
  };

  useSocket(socket, true, updateChats, handleError); // Passa handleError para o hook

  useEffect(() => {
    if (selectedChat) {
      const companyCode = localStorage.getItem('companyCode');
      socket.emit('get_messages', {
        chatId: selectedChat.chatId,
        ticketNumber: selectedChat.TicketNumber,
        company_code: companyCode || undefined
      });
    }
  }, [selectedChat, socket]);

  const handleChatSelect = (chat) => {
    setSelectedChat(chat);
    setSelectedChatId(chat.chatId);  // Atualiza o selectedChatId
    markAsRead(chat.chatId);
  };

  const handleChatClose = () => {
    setSelectedChat(null);
    setSelectedChatId(null);  // Reseta o selectedChatId ao fechar o chat
  };

  const handlePullSupport = (ticketNumber) => {
    const selected = chats.find((chat) => chat.TicketNumber === ticketNumber);
    const attendantName = localStorage.getItem('attendantName') || userId || 'Atendente';
    axios.post(`${apiBaseUrl}/chats/${ticketNumber}/pull-support`, {
      userId,
      attendantName
    })
      .then(() => {
        if (selected?.chatId) {
          updateChats({
            chatId: selected.chatId,
            TicketNumber: ticketNumber,
            status: 'em_aberto',
            userId,
            atendente: attendantName,
          });
        }
      })
      .catch(() => {});
  };

  const renderContent = () => {
    if (showChangePassword) {
      return <ChangePassword onLogout={handleLogout} />;
    }

    if (activeTab === 'dashboard') {
      return <Dashboard />; // Renderiza o Dashboard se a aba ativa for 'dashboard'
    }

    if (activeTab === 'gestao') {
      return <TicketManager chats={chats} userId={userId} onPullSupport={handlePullSupport} />;
    }

    if (activeTab === 'relatorios') {
      return <ReportManager />;
    }

    if (activeTab === 'comercial') {
      return <CommercialPlaceholder />;
    }

    if (activeTab === 'satisfacao') {
      return <NpsManager />;
    }

    if (activeTab === 'cadastros') {
      return <CadastroPanel />;
    }

    if (activeTab === 'knowledge') {
      return <KnowledgeManager />;
    }

    if (activeTab === 'projetos') {
      return <KanbanBoard projectDomain="sw" />;
    }

    if (activeTab === 'projetos_rma') {
      return <KanbanBoard projectDomain="rma_hw" />;
    }

    if (activeTab === 'configuracoes') {
      return <SettingsPanel role={role} />;
    }

    const selectedQueue = availableChatCategories.find((queue) => queue.id === chatCategory) || availableChatCategories[0];
    const queueMatcher = (chat, queue) => {
      if (!queue) return true;
      const status = chat.status || '';
      const assignedUser = String(chat.userId || '').trim();
      if (queue.id === 'meus_chats') {
        return assignedUser === String(userId || '').trim() && !FINAL_QUEUE_STATUSES.has(status);
      }
      if (queue.id === 'nao_atribuidos') {
        return !assignedUser && ASSIGNMENT_QUEUE_STATUSES.has(status);
      }
      if (queue.showAll) {
        return true;
      }
      return queue.statuses ? queue.statuses.includes(status) : true;
    };

    const queueCounts = availableChatCategories.reduce((acc, queue) => {
      acc[queue.id] = chats.filter((chat) => queueMatcher(chat, queue)).length;
      return acc;
    }, {});

    // Código existente para as outras abas
    const filteredChats = chats.filter(chat => {
      const phone = chat.customer_phone || (chat.chatId ? chat.chatId.split('@')[0] : '');
      const matchSearchTerm = (chat.customer_name && chat.customer_name.toLowerCase().includes(searchTerm.toLowerCase())) ||
                              (chat.name && chat.name.toLowerCase().includes(searchTerm.toLowerCase())) ||
                              (phone && phone.toLowerCase().includes(searchTerm.toLowerCase())) ||
                              (chat.TicketNumber && chat.TicketNumber.toLowerCase().includes(searchTerm.toLowerCase()));
      return queueMatcher(chat, selectedQueue) && matchSearchTerm;
    });

    return (
      <div className="content chat-container">
        <div className="chat-sidebar">
          <div className="sidebar-header">
            <div>
              <h3>Multi Atendimento</h3>
              <p>Gerencie filas e conversas em tempo real.</p>
            </div>
          </div>
          <input
            type="text"
            placeholder="Pesquisar chats..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="search-input"
          />
          <div className="sidebar-buttons">
            {availableChatCategories.map((queue) => (
              <button
                key={queue.id}
                className={chatCategory === queue.id ? 'active' : ''}
                onClick={() => setChatCategory(queue.id)}
              >
                <span>{queue.label}</span>
                <span className="queue-count">{queueCounts[queue.id] || 0}</span>
              </button>
            ))}
          </div>
        </div>
        <div className="chat-section">
          <ChatList
            chats={filteredChats}
            onChatSelect={handleChatSelect}
            onPullSupport={handlePullSupport}
            onMarkAsRead={markAsRead}
            selectedChatId={selectedChatId} // Passa o selectedChatId para ChatList
          />
          {selectedChat && (
            <ChatWindow
              chat={selectedChat}
              socket={socket}
              onMarkAsRead={markAsRead}
              onClose={handleChatClose}
            />
          )}
        </div>
      </div>
    );
  };

  return (
    <>
      {errorMessage && (
        <div className="error-message">
          {errorMessage}
        </div>
      )}
      <nav className="nav">
        <ul>
          {visibleTabs.map((tab) => (
            <li
              key={tab.id}
              className={`${tab.config ? 'config ' : ''}${activeTab === tab.id ? 'active' : ''}`}
              onClick={() => setActiveTab(tab.id)}
            >
              {tab.label}
            </li>
          ))}
        </ul>
        <div className="nav-buttons">
          <button className="nav-button" onClick={() => setShowAreaSwitcher(true)}>
            Área: {AREA_CARDS[activeArea]?.title || activeArea || 'N/A'}
          </button>
          <button className="nav-button" onClick={() => setShowChangePassword(true)}>Trocar Senha</button>
          <button className="nav-button" onClick={handleLogout}>Logout</button>
        </div>
      </nav>
      <main className={`main-panel ${activeTab === 'chamados' || activeTab === 'gestao' ? 'main-panel--chat' : 'main-panel--scroll'}`}>
        {!accessLoaded && <div className="content"><p>Carregando perfil de acesso...</p></div>}
        {accessLoaded && renderContent()}
      </main>
      {showAreaSwitcher && (
        <div
          className="area-switcher-backdrop"
          onMouseDown={(event) => {
            if (event.target.classList.contains('area-switcher-backdrop')) {
              setShowAreaSwitcher(false);
            }
          }}
        >
          <div className="area-switcher-modal" onMouseDown={(event) => event.stopPropagation()}>
            <div className="area-switcher-modal__head">
              <div>
                <h3>Trocar Área de Trabalho</h3>
                <p>Altere rapidamente o painel ativo da sua sessão.</p>
              </div>
              <button className="ghost" onClick={() => setShowAreaSwitcher(false)}>Fechar</button>
            </div>
            <div className="area-switcher-grid">
              {areaOptions.map((option) => (
                <button
                  key={option.id}
                  className={`area-switcher-card ${activeArea === option.id ? 'active' : ''} ${option.enabled ? '' : 'disabled'}`}
                  disabled={!option.enabled}
                  onClick={() => {
                    setActiveArea(option.id);
                    localStorage.setItem('activeArea', option.id);
                    localStorage.removeItem('pendingAreaSelection');
                    setShowAreaSwitcher(false);
                  }}
                >
                  <div className="area-switcher-card__top">
                    <strong>{option.title}</strong>
                    <span className={`area-switcher-status ${option.enabled ? 'ok' : 'off'}`}>
                      {option.enabled ? 'Disponível' : 'Indisponível'}
                    </span>
                  </div>
                  <p>{option.description}</p>
                  {!option.enabled && <small>{option.reason}</small>}
                </button>
              ))}
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default MainContent;
